import { emptyEldvaktApi } from "src/common/service/eldvakt/emptyEldvaktApi.ts";
import type {
	CreateSkillRequest,
	GenerateSkillRequest,
	GenerateSkillResponse,
	ParseUploadResponse,
	SkillResponse,
	UpdateSkillRequest,
} from "./skillTypes";

export const aiSkillsApi = emptyEldvaktApi.injectEndpoints({
	endpoints: (builder) => ({
		getSkills: builder.query<SkillResponse[], string>({
			query: (companyDomainId) =>
				`ai/skills?companyDomainId=${companyDomainId}`,
			providesTags: (result) =>
				result
					? [
							"AiSkill",
							...result.map((s) => ({
								type: "AiSkill" as const,
								id: s.id,
							})),
						]
					: ["AiSkill"],
		}),

		getSkill: builder.query<
			SkillResponse,
			{ companyDomainId: string; skillId: string }
		>({
			query: ({ companyDomainId, skillId }) =>
				`ai/skills/${skillId}?companyDomainId=${companyDomainId}`,
			providesTags: (_result, _error, { skillId }) => [
				{ type: "AiSkill", id: skillId },
			],
		}),

		createSkill: builder.mutation<SkillResponse, CreateSkillRequest>({
			query: ({ companyDomainId, ...body }) => ({
				url: `ai/skills?companyDomainId=${companyDomainId}`,
				method: "POST",
				body,
			}),
			invalidatesTags: ["AiSkill"],
		}),

		updateSkill: builder.mutation<SkillResponse, UpdateSkillRequest>({
			query: ({ companyDomainId, skillId, ...body }) => ({
				url: `ai/skills/${skillId}?companyDomainId=${companyDomainId}`,
				method: "PUT",
				body,
			}),
			async onQueryStarted(
				{ companyDomainId, skillId, ...patch },
				{ dispatch, queryFulfilled },
			) {
				// Optimistic update for isEnabled toggle
				const patchList = dispatch(
					aiSkillsApi.util.updateQueryData(
						"getSkills",
						companyDomainId,
						(draft) => {
							const skill = draft.find((s) => s.id === skillId);
							if (skill) {
								Object.assign(skill, patch);
							}
						},
					),
				);
				try {
					await queryFulfilled;
				} catch {
					patchList.undo();
				}
			},
			invalidatesTags: ["AiSkill"],
		}),

		deleteSkill: builder.mutation<
			void,
			{ companyDomainId: string; skillId: string }
		>({
			query: ({ companyDomainId, skillId }) => ({
				url: `ai/skills/${skillId}?companyDomainId=${companyDomainId}`,
				method: "DELETE",
			}),
			invalidatesTags: ["AiSkill"],
		}),

		generateSkill: builder.mutation<
			GenerateSkillResponse,
			GenerateSkillRequest
		>({
			query: ({ companyDomainId, ...body }) => ({
				url: `ai/skills/generate?companyDomainId=${companyDomainId}`,
				method: "POST",
				body,
			}),
		}),

		parseSkillUpload: builder.mutation<
			ParseUploadResponse,
			{ companyDomainId: string; file: File }
		>({
			query: ({ companyDomainId, file }) => {
				const formData = new FormData();
				formData.append("file", file);
				return {
					url: `ai/skills/parse-upload?companyDomainId=${companyDomainId}`,
					method: "POST",
					body: formData,
				};
			},
		}),
	}),
});

export const {
	useGetSkillsQuery,
	useGetSkillQuery,
	useCreateSkillMutation,
	useUpdateSkillMutation,
	useDeleteSkillMutation,
	useGenerateSkillMutation,
	useParseSkillUploadMutation,
} = aiSkillsApi;
